import { useState, useEffect } from 'react';
import { 
  Settings, MapPin, Clock, Bell, Save, RefreshCw,
  Sun, Moon, DoorOpen, Eye, AlertCircle, Check, ChevronDown, HelpCircle
} from 'lucide-react';
import fetchWithTenant from '../utils/fetchWithTenant';
import { toast } from 'react-toastify';
import useTutorialStore from '../stores/tutorialStore';
import useTutorialPrompt from '../hooks/useTutorialPrompt';
import TutorialPrompt from '../components/tutorials/TutorialPrompt';
import shabbatTutorial from '../components/tutorials/configs/shabbatTutorial';

const ShabbatSettings = () => {
  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const { showPrompt, dismissTutorial } = useTutorialPrompt(shabbatTutorial);

  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [doors, setDoors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingDoors, setLoadingDoors] = useState(false);
  const [upcomingJobs, setUpcomingJobs] = useState([]);
  const [expandedSection, setExpandedSection] = useState('location');

  const [testResults, setTestResults] = useState(null);
  const [testing, setTesting] = useState(false);
  const [notificationUsers, setNotificationUsers] = useState([]);
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [configRes, statusRes, integrationsRes, jobsRes, usersRes] = await Promise.all([
        fetchWithTenant('/api/shabbat/config'),
        fetchWithTenant('/api/shabbat/status'),
        fetchWithTenant('/api/integrations'),
        fetchWithTenant('/api/shabbat/jobs'),
        fetchWithTenant('/api/users')
      ]);

      if (configRes.ok) {
        const data = await configRes.json();
        setConfig(data);
      }

      if (statusRes.ok) {
        const data = await statusRes.json();
        setStatus(data);
      }

      if (integrationsRes.ok) {
        const data = await integrationsRes.json();
        setIntegrations(data.integrations || []);
      }

      if (jobsRes.ok) {
        const data = await jobsRes.json();
        setUpcomingJobs(Array.isArray(data) ? data : []);
      }
      if (usersRes.ok) {
        const data = await usersRes.json();
        const eligible = (data.users || []).filter(u => u.canActivateShabbat || u.role?.toUpperCase() === "ADMIN");
        setNotificationUsers(eligible);
      }
    } catch (error) {
      console.error('Failed to load Shabbat config:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const loadDoors = async () => {
    setLoadingDoors(true);
    try {
      const res = await fetchWithTenant('/api/acs/doors');
      if (res.ok) {
        const data = await res.json();
        setDoors(data.doors || data.readers || data.data || []);
      }
    } catch (error) {
      console.error('Failed to load doors:', error);
    } finally {
      setLoadingDoors(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetchWithTenant('/api/shabbat/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });

      if (res.ok) {
        toast.success('Shabbat settings saved');
        loadData();
      } else {
        const data = await res.json();
        toast.error(data.error || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };


  const handleTestWorkflow = async () => {
    setTesting(true);
    setTestResults(null);
    try {
      const res = await fetchWithTenant("/api/shabbat/test-workflow", {
        method: "POST"
      });
      if (res.ok) {
        const data = await res.json();
        setTestResults(data);
        setExpandedSection("test-results");
      } else {
        const data = await res.json();
        toast.error(data.error || "Test failed");
      }
    } catch (error) {
      toast.error("Failed to run test");
    } finally {
      setTesting(false);
    }
  };
  const updateConfig = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const accessControlIntegrations = integrations.filter(i => 
    i.type?.includes('ACCESS') || i.categoryName === 'ACCESS_CONTROL'
  );

  const aiIntegrations = integrations.filter(i => 
    i.type?.includes('AI') || i.type?.includes('OOSTO') || i.categoryName === 'AI_ANALYTICS'
  );

  // Get selected FR integration name
  const selectedFRIntegration = aiIntegrations.find(i => i.id === config?.oostoIntegrationId);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
    if (section === 'doors' && doors.length === 0) {
      loadDoors();
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'Not scheduled';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Consistent dropdown style matching admin page
  const selectStyle = {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'var(--bg-page)',
    border: '0.5px solid var(--border)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '14px',
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 10px center',
    backgroundSize: '20px'
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'var(--bg-page)',
    border: '0.5px solid var(--border)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '14px'
  };

  const badgeStyle = {
    fontSize: '12px',
    color: '#10b981',
    background: 'rgba(16, 185, 129, 0.2)',
    padding: '2px 10px',
    borderRadius: '4px',
    fontWeight: '500'
  };

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <RefreshCw className="spin" size={32} />
          <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>Loading Shabbat settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="md-page">
      <div className="page-header-with-actions">
        <div className="page-header-content">
          <h1 className="page-title">
            <Sun size={22} />
            Shabbat Mode Settings
          </h1>
          <p className="page-subtitle">
            Configure automatic Shabbat scheduling for door access and facial recognition
          </p>
        </div>
        <div className="page-header-actions">
          <button
            className="btn btn-secondary"
            onClick={handleTestWorkflow}
            disabled={testing}
          >
            {testing ? <><RefreshCw size={16} className="spin" /> Testing...</> : <><Settings size={16} /> Test Workflow</>}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <><RefreshCw size={16} className="spin" /> Saving...</> : <><Save size={16} /> Save Changes</>}
          </button>
          <button className="tutorial-help-btn" onClick={() => startTutorial(shabbatTutorial.module, shabbatTutorial.steps)} title="Take a tour" aria-label="Take a tour">
            <HelpCircle size={16} />
          </button>
        </div>
      </div>

      {/* Status Card */}
      {status && (
        <div style={{
          background: status.isActive 
            ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.2), rgba(126, 34, 206, 0.3))'
            : 'rgba(255,255,255,0.03)',
          border: status.isActive 
            ? '1px solid rgba(147, 51, 234, 0.5)'
            : '1px solid rgba(255,255,255,0.1)',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {status.isActive ? <Moon size={24} color="#a855f7" /> : <Sun size={24} color="#888" />}
              <div>
                <div style={{ fontWeight: '600', fontSize: '18px' }}>
                  {status.isActive ? 'Shabbat Mode Active' : 'Shabbat Mode Inactive'}
                </div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                  {status.nextJob 
                    ? `Next: ${status.nextJob.action} at ${formatDateTime(status.nextJob.scheduledFor)}`
                    : 'No upcoming actions scheduled'
                  }
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Enabled</span>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={config?.isEnabled || false}
                  onChange={(e) => updateConfig('isEnabled', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Configuration Sections */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        
        {/* Location Section */}
        <div className="settings-section">
          <button 
            className="section-header"
            onClick={() => toggleSection('location')}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 20px',
              background: 'rgba(255,255,255,0.05)',
              border: 'none',
              borderRadius: expandedSection === 'location' ? '12px 12px 0 0' : '12px',
              color: '#e5e5e5',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <MapPin size={20} />
              <span style={{ fontWeight: '500' }}>Location & Timezone</span>
              {config?.zipCode && (
                <span style={badgeStyle}>{config.zipCode}</span>
              )}
            </div>
            <ChevronDown 
              size={20} 
              style={{ 
                transform: expandedSection === 'location' ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }} 
            />
          </button>
          {expandedSection === 'location' && (
            <div style={{
              padding: '20px',
              background: 'rgba(255,255,255,0.03)',
              borderRadius: '0 0 12px 12px',
              borderTop: '1px solid rgba(255,255,255,0.1)'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    style={inputStyle}
                    placeholder="e.g., 29401"
                    value={config?.zipCode || ''}
                    onChange={(e) => updateConfig('zipCode', e.target.value)}
                  />
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    Used to calculate sunset times for your location
                  </p>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Timezone
                  </label>
                  <select
                    style={selectStyle}
                    value={config?.timezone || 'America/New_York'}
                    onChange={(e) => updateConfig('timezone', e.target.value)}
                  >
                    <option value="America/New_York">Eastern Time</option>
                    <option value="America/Chicago">Central Time</option>
                    <option value="America/Denver">Mountain Time</option>
                    <option value="America/Los_Angeles">Pacific Time</option>
                    <option value="America/Phoenix">Arizona Time</option>
                  </select>
                </div>
              </div>
              {config?.latitude && config?.longitude && (
                <div style={{ 
                  marginTop: '16px', 
                  padding: '12px', 
                  background: 'rgba(16, 185, 129, 0.1)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#10b981',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Check size={16} />
                  Coordinates: {config.latitude.toFixed(4)}, {config.longitude.toFixed(4)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Door Configuration Section */}
        <div className="settings-section">
          <button 
            className="section-header"
            onClick={() => toggleSection('doors')}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 20px',
              background: 'rgba(255,255,255,0.05)',
              border: 'none',
              borderRadius: expandedSection === 'doors' ? '12px 12px 0 0' : '12px',
              color: '#e5e5e5',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <DoorOpen size={20} />
              <span style={{ fontWeight: '500' }}>Front Door Configuration</span>
              {config?.frontDoorName && (
                <span style={badgeStyle}>{config.frontDoorName}</span>
              )}
            </div>
            <ChevronDown 
              size={20} 
              style={{ 
                transform: expandedSection === 'doors' ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }} 
            />
          </button>
          {expandedSection === 'doors' && (
            <div style={{
              padding: '20px',
              background: 'rgba(255,255,255,0.03)',
              borderRadius: '0 0 12px 12px',
              borderTop: '1px solid rgba(255,255,255,0.1)'
            }}>
              {accessControlIntegrations.length === 0 ? (
                <div style={{ 
                  padding: '20px', 
                  textAlign: 'center', 
                  color: 'var(--text-secondary)',
                  background: 'rgba(239, 68, 68, 0.1)',
                  borderRadius: '8px'
                }}>
                  <AlertCircle size={24} style={{ marginBottom: '8px' }} />
                  <p>No access control integration configured.</p>
                  <p style={{ fontSize: '14px' }}>Go to Admin Settings → Integrations to add one.</p>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Select Front Door
                    </label>
                    {loadingDoors ? (
                      <div style={{ color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <RefreshCw size={16} className="spin" /> Loading doors...
                      </div>
                    ) : doors.length === 0 ? (
                      <button className="btn btn-secondary" onClick={loadDoors}>
                        Load Doors from Access Control
                      </button>
                    ) : (
                      <select
                        style={selectStyle}
                        value={config?.frontDoorReaderId || ''}
                        onChange={(e) => {
                          const door = doors.find(d => d.Key === e.target.value || d.id === e.target.value);
                          updateConfig('frontDoorReaderId', e.target.value);
                          updateConfig('frontDoorReaderKey', door?.Key || e.target.value);
                          updateConfig('frontDoorName', door?.CommonName || door?.name || 'Front Door');
                        }}
                      >
                        <option value="">-- Select a door --</option>
                        {doors.map(door => (
                          <option key={door.Key || door.id} value={door.Key || door.id}>
                            {door.CommonName || door.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Or Enter Door ID Manually
                    </label>
                    <input
                      type="text"
                      style={inputStyle}
                      placeholder="Reader Key or ID"
                      value={config?.frontDoorReaderKey || ''}
                      onChange={(e) => {
                        updateConfig('frontDoorReaderKey', e.target.value);
                        updateConfig('frontDoorReaderId', e.target.value);
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Schedule Section */}
        <div className="settings-section">
          <button 
            className="section-header"
            onClick={() => toggleSection('schedule')}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 20px',
              background: 'rgba(255,255,255,0.05)',
              border: 'none',
              borderRadius: expandedSection === 'schedule' ? '12px 12px 0 0' : '12px',
              color: '#e5e5e5',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Clock size={20} />
              <span style={{ fontWeight: '500' }}>Schedule Configuration</span>
            </div>
            <ChevronDown 
              size={20} 
              style={{ 
                transform: expandedSection === 'schedule' ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }} 
            />
          </button>
          {expandedSection === 'schedule' && (
            <div style={{
              padding: '20px',
              background: 'rgba(255,255,255,0.03)',
              borderRadius: '0 0 12px 12px',
              borderTop: '1px solid rgba(255,255,255,0.1)'
            }}>
              {/* Friday Settings */}
              <h4 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Moon size={18} /> Friday Evening
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Sunset Offset (minutes)
                  </label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={config?.fridaySunsetOffsetMins ?? -5}
                    onChange={(e) => updateConfig('fridaySunsetOffsetMins', parseInt(e.target.value))}
                  />
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    Negative = before sunset, Positive = after sunset
                  </p>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Action at Sunset
                  </label>
                  <select
                    style={selectStyle}
                    value={config?.fridaySunsetAction || 'disable_fr'}
                    onChange={(e) => updateConfig('fridaySunsetAction', e.target.value)}
                  >
                    <option value="disable_fr">Disable Facial Recognition</option>
                    <option value="unlock_door">Unlock Front Door</option>
                    <option value="both">Both (Disable FR + Unlock)</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Friday Unlock Time
                  </label>
                  <input
                    type="time"
                    style={inputStyle}
                    value={config?.fridayUnlockTime || '18:30'}
                    onChange={(e) => updateConfig('fridayUnlockTime', e.target.value)}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Friday Lock Time
                  </label>
                  <input
                    type="time"
                    style={inputStyle}
                    value={config?.fridayLockTime || '19:30'}
                    onChange={(e) => updateConfig('fridayLockTime', e.target.value)}
                  />
                </div>
              </div>

              {/* Saturday Settings */}
              <h4 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Sun size={18} /> Saturday
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Saturday Unlock Time
                  </label>
                  <input
                    type="time"
                    style={inputStyle}
                    value={config?.saturdayUnlockTime || '10:00'}
                    onChange={(e) => updateConfig('saturdayUnlockTime', e.target.value)}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Saturday Lock Time
                  </label>
                  <input
                    type="time"
                    style={inputStyle}
                    value={config?.saturdayLockTime || '11:30'}
                    onChange={(e) => updateConfig('saturdayLockTime', e.target.value)}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Sunset End Offset (minutes)
                  </label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={config?.saturdaySunsetOffsetMins ?? 30}
                    onChange={(e) => updateConfig('saturdaySunsetOffsetMins', parseInt(e.target.value))}
                  />
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    Minutes after Saturday sunset to re-enable FR
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Facial Recognition Section */}
        <div className="settings-section">
          <button 
            className="section-header"
            onClick={() => toggleSection('fr')}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 20px',
              background: 'rgba(255,255,255,0.05)',
              border: 'none',
              borderRadius: expandedSection === 'fr' ? '12px 12px 0 0' : '12px',
              color: '#e5e5e5',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Eye size={20} />
              <span style={{ fontWeight: '500' }}>Facial Recognition</span>
              {selectedFRIntegration && (
                <span style={badgeStyle}>{selectedFRIntegration.name || selectedFRIntegration.displayName}</span>
              )}
            </div>
            <ChevronDown 
              size={20} 
              style={{ 
                transform: expandedSection === 'fr' ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }} 
            />
          </button>
          {expandedSection === 'fr' && (
            <div style={{
              padding: '20px',
              background: 'rgba(255,255,255,0.03)',
              borderRadius: '0 0 12px 12px',
              borderTop: '1px solid rgba(255,255,255,0.1)'
            }}>
              {aiIntegrations.length === 0 ? (
                <div style={{ 
                  padding: '20px', 
                  textAlign: 'center', 
                  color: 'var(--text-secondary)',
                  background: 'rgba(255, 193, 7, 0.1)',
                  borderRadius: '8px'
                }}>
                  <AlertCircle size={24} style={{ marginBottom: '8px', color: '#ffc107' }} />
                  <p>No AI/Facial Recognition integration configured.</p>
                  <p style={{ fontSize: '14px' }}>This is optional. Go to Admin Settings → Integrations to add one.</p>
                </div>
              ) : (
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    FR Integration
                  </label>
                  <select
                    style={selectStyle}
                    value={config?.oostoIntegrationId || ''}
                    onChange={(e) => updateConfig('oostoIntegrationId', e.target.value)}
                  >
                    <option value="">-- Select integration --</option>
                    {aiIntegrations.map(int => (
                      <option key={int.id} value={int.id}>
                        {int.name || int.displayName}
                      </option>
                    ))}
                  </select>
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '8px' }}>
                    Facial recognition will be disabled during Shabbat and re-enabled after.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Notifications Section */}
        <div className="settings-section">
          <button
            className="section-header"
            onClick={() => toggleSection('notifications')}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 20px',
              background: 'rgba(255,255,255,0.05)',
              border: 'none',
              borderRadius: expandedSection === 'notifications' ? '12px 12px 0 0' : '12px',
              color: '#e5e5e5',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Bell size={20} />
              <span style={{ fontWeight: '500' }}>Notifications</span>
              {(config?.notificationEmails?.length > 0) && (
                <span style={{
                  background: 'rgba(16, 185, 129, 0.2)',
                  color: '#10b981',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '12px'
                }}>
                  {config.notificationEmails.length} recipient{config.notificationEmails.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <ChevronDown
              size={20}
              style={{
                transform: expandedSection === 'notifications' ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }}
            />
          </button>
          {expandedSection === 'notifications' && (
            <div style={{
              padding: '20px',
              background: 'rgba(255,255,255,0.03)',
              borderRadius: '0 0 12px 12px',
              borderTop: '1px solid rgba(255,255,255,0.1)'
            }}>
              {/* Notification Toggles */}
              <div style={{ display: 'flex', gap: '24px', marginBottom: '24px', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={config?.notifyOnSuccess ?? true}
                    onChange={(e) => updateConfig('notifyOnSuccess', e.target.checked)}
                    style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }}
                  />
                  <span>Notify on successful actions</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={config?.notifyOnFailure ?? true}
                    onChange={(e) => updateConfig('notifyOnFailure', e.target.checked)}
                    style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }}
                  />
                  <span>Notify on failures</span>
                  <span style={{ color: '#ef4444', fontSize: '12px' }}>(Recommended)</span>
                </label>
              </div>

              {/* Email Recipients */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Email Recipients
                </label>
                <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '12px' }}>
                  These email addresses will receive Shabbat mode notifications.
                </p>
                
                {/* Email list */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                  {(config?.notificationEmails || []).map((email, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: 'rgba(255,255,255,0.1)',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}>
                      <span>{email}</span>
                      <button
                        onClick={() => {
                          const updated = config.notificationEmails.filter((_, i) => i !== idx);
                          updateConfig('notificationEmails', updated);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '2px',
                          color: 'var(--text-secondary)',
                          display: 'flex'
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  {(!config?.notificationEmails || config.notificationEmails.length === 0) && (
                    <span style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}>No email recipients configured</span>
                  )}
                </div>
                
                {/* Add email input */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="email"
                    id="newEmailInput"
                    style={inputStyle}
                    placeholder="Enter email address"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        const input = e.target;
                        const email = input.value.trim().toLowerCase();
                        if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                          const current = config?.notificationEmails || [];
                          if (!current.includes(email)) {
                            updateConfig('notificationEmails', [...current, email]);
                            input.value = '';
                          }
                        }
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      const input = document.getElementById('newEmailInput');
                      const email = input.value.trim().toLowerCase();
                      if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                        const current = config?.notificationEmails || [];
                        if (!current.includes(email)) {
                          updateConfig('notificationEmails', [...current, email]);
                          input.value = '';
                        }
                      }
                    }}
                    style={{
                      padding: '10px 16px',
                      background: 'rgba(255,255,255,0.1)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    + Add
                  </button>
                </div>
              </div>


              {/* Push Notification Recipients */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Push Notification Recipients
                </label>
                <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '12px' }}>
                  Select users to receive push notifications on their devices.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {(notificationUsers || []).length === 0 ? (
                    <span style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}>Loading users...</span>
                  ) : (
                    (notificationUsers || [])
                      .filter(u => u.canActivateShabbat || u.role?.toUpperCase() === 'ADMIN')
                      .map(user => (
                        <label
                          key={user.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '12px 16px',
                            background: (config?.notificationPushUserIds || []).includes(user.id) 
                              ? 'rgba(16, 185, 129, 0.1)' 
                              : 'rgba(255,255,255,0.05)',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            border: (config?.notificationPushUserIds || []).includes(user.id)
                              ? '1px solid rgba(16, 185, 129, 0.3)'
                              : '1px solid transparent'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={(config?.notificationPushUserIds || []).includes(user.id)}
                            onChange={() => {
                              const current = config?.notificationPushUserIds || [];
                              const updated = current.includes(user.id)
                                ? current.filter(id => id !== user.id)
                                : [...current, user.id];
                              updateConfig('notificationPushUserIds', updated);
                            }}
                            style={{ width: '18px', height: '18px', accentColor: '#10b981' }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '500' }}>{user.name || user.email}</div>
                            {user.name && <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{user.email}</div>}
                          </div>
                          {user.role?.toUpperCase() === 'ADMIN' && (
                            <span style={{
                              fontSize: '11px',
                              background: 'rgba(59, 130, 246, 0.2)',
                              color: '#60a5fa',
                              padding: '2px 8px',
                              borderRadius: '4px'
                            }}>
                              Admin
                            </span>
                          )}
                        </label>
                      ))
                  )}
                </div>
              </div>
              {/* Slack Webhook */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Slack Webhook URL (optional)
                </label>
                <input
                  type="text"
                  style={inputStyle}
                  placeholder="https://hooks.slack.com/services/..."
                  value={config?.slackWebhookUrl || ''}
                  onChange={(e) => updateConfig('slackWebhookUrl', e.target.value)}
                />
              </div>

              {/* Test Notification */}
              <div style={{
                borderTop: '1px solid rgba(255,255,255,0.1)',
                paddingTop: '20px'
              }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Test Notifications
                </label>
                <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '12px' }}>
                  Send a test email to verify your notification settings.
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="email"
                    id="testEmailInput"
                    style={inputStyle}
                    placeholder={config?.notificationEmails?.[0] || 'Enter test email'}
                  />
                  <button
                    onClick={async () => {
                      const input = document.getElementById('testEmailInput');
                      const email = input.value.trim() || config?.notificationEmails?.[0];
                      if (!email) {
                        toast.error('Please enter an email address');
                        return;
                      }
                      try {
                        const res = await fetchWithTenant('/api/shabbat/test-notification', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ email })
                        });
                        if (res.ok) {
                          toast.success(`Test email sent to ${email}`);
                        } else {
                          const data = await res.json();
                          toast.error(data.error || 'Failed to send test email');
                        }
                      } catch (err) {
                        toast.error('Failed to send test email');
                      }
                    }}
                    style={{
                      padding: '10px 20px',
                      background: '#3b82f6',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontWeight: '500'
                    }}
                  >
                    Send Test
                  </button>
                </div>
              </div>

              {/* Info box */}
              <div style={{
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '8px',
                padding: '16px',
                marginTop: '20px'
              }}>
                <div style={{ fontWeight: '500', marginBottom: '8px', color: '#60a5fa' }}>
                  How notifications work:
                </div>
                <ul style={{ margin: 0, paddingLeft: '20px', color: 'var(--text-secondary)', fontSize: '13px', lineHeight: '1.8' }}>
                  <li>You'll receive a notification at each step of the Shabbat cycle</li>
                  <li>Each notification shows what just happened and what's coming next</li>
                  <li>If an action fails, the system will retry once after 30 seconds</li>
                  <li>If the retry also fails, you'll receive a detailed alert with instructions</li>
                </ul>
              </div>
            </div>
          )}
        </div>
        {/* Upcoming Jobs */}
        {upcomingJobs.length > 0 && (
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            borderRadius: '12px',
            padding: '20px',
            marginTop: '8px'
          }}>
            <h3 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Clock size={20} />
              Upcoming Scheduled Jobs
            </h3>
            <div style={{ display: 'grid', gap: '8px' }}>
              {upcomingJobs.slice(0, 6).map(job => (
                <div key={job.id} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '8px'
                }}>
                  <span>{job.actionName}</span>
                  <span style={{ color: 'var(--text-secondary)' }}>{formatDateTime(job.scheduledFor)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Test Results */}
        {testResults && (
          <div style={{
            background: "rgba(255,255,255,0.03)",
            borderRadius: "12px",
            padding: "20px",
            marginTop: "16px"
          }}>
            <h3 style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
              <Settings size={20} />
              Test Workflow Results
              <span style={{
                marginLeft: "auto",
                padding: "4px 12px",
                borderRadius: "4px",
                fontSize: "12px",
                fontWeight: "500",
                background: testResults.summary?.ready ? "rgba(16, 185, 129, 0.2)" : "rgba(239, 68, 68, 0.2)",
                color: testResults.summary?.ready ? "#10b981" : "#ef4444"
              }}>
                {testResults.summary?.ready ? "Ready" : "Not Ready"}
              </span>
            </h3>
            
            {testResults.summary && (
              <div style={{
                padding: "12px 16px",
                marginBottom: "16px",
                borderRadius: "8px",
                background: testResults.summary.ready ? "rgba(16, 185, 129, 0.1)" : "rgba(239, 68, 68, 0.1)",
                border: testResults.summary.ready ? "1px solid rgba(16, 185, 129, 0.3)" : "1px solid rgba(239, 68, 68, 0.3)"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {testResults.summary.ready ? <Check size={18} color="#10b981" /> : <AlertCircle size={18} color="#ef4444" />}
                  <span style={{ color: testResults.summary.ready ? "#10b981" : "#ef4444" }}>
                    {testResults.summary.message}
                  </span>
                </div>
              </div>
            )}
            
            <div style={{ display: "grid", gap: "12px" }}>
              {testResults.steps?.map((step, idx) => (
                <div key={idx} style={{
                  padding: "12px 16px",
                  background: "rgba(255,255,255,0.05)",
                  borderRadius: "8px",
                  borderLeft: step.status === "success" ? "3px solid #10b981" :
                              step.status === "error" ? "3px solid #ef4444" :
                              step.status === "warning" ? "3px solid #f59e0b" : "3px solid #6b7280"
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                    <span style={{ fontWeight: "500" }}>Step {step.step}: {step.name}</span>
                    <span style={{
                      fontSize: "12px",
                      padding: "2px 8px",
                      borderRadius: "4px",
                      background: step.status === "success" ? "rgba(16, 185, 129, 0.2)" :
                                  step.status === "error" ? "rgba(239, 68, 68, 0.2)" :
                                  step.status === "warning" ? "rgba(245, 158, 11, 0.2)" : "rgba(107, 114, 128, 0.2)",
                      color: step.status === "success" ? "#10b981" :
                             step.status === "error" ? "#ef4444" :
                             step.status === "warning" ? "#f59e0b" : "#9ca3af"
                    }}>
                      {step.status}
                    </span>
                  </div>
                  {step.details && typeof step.details === "string" && (
                    <div style={{ fontSize: "13px", color: 'var(--text-secondary)' }}>{step.details}</div>
                  )}
                  {step.details && typeof step.details === "object" && (
                    <div style={{ fontSize: "13px", color: 'var(--text-secondary)' }}>
                      {Object.entries(step.details).map(([k, v]) => (
                        <div key={k}><strong>{k}:</strong> {String(v)}</div>
                      ))}
                    </div>
                  )}
                  {step.connectionTest && (
                    <div style={{ marginTop: "8px", fontSize: "13px" }}>
                      <span style={{ color: step.connectionTest.success ? "#10b981" : "#ef4444" }}>
                        Connection: {step.connectionTest.success ? "OK" : step.connectionTest.error}
                      </span>
                    </div>
                  )}
                  {step.devices && (
                    <div style={{ marginTop: "12px" }}>
                      <div style={{ fontSize: "12px", color: 'var(--text-secondary)', marginBottom: "8px" }}>
                        Cameras ({step.devices.active} active of {step.devices.total}):
                      </div>
                      <div style={{ maxHeight: "150px", overflowY: "auto" }}>
                        {step.devices.list.map((device, didx) => (
                          <div key={didx} style={{
                            display: "flex",
                            justifyContent: "space-between",
                            padding: "6px 12px",
                            marginBottom: "2px",
                            background: "rgba(255,255,255,0.03)",
                            borderRadius: "4px",
                            fontSize: "12px"
                          }}>
                            <span>{device.name}</span>
                            <span style={{ color: device.status === "Active" ? "#10b981" : "#6b7280" }}>
                              {device.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {step.actions && (
                    <div style={{ marginTop: "12px" }}>
                      <div style={{ fontSize: "12px", color: 'var(--text-secondary)', marginBottom: "8px" }}>Scheduled Actions:</div>
                      {step.actions.map((action, aidx) => (
                        <div key={aidx} style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "8px 12px",
                          marginBottom: "4px",
                          background: "rgba(255,255,255,0.03)",
                          borderRadius: "4px",
                          fontSize: "13px"
                        }}>
                          <span>{action.description}</span>
                          <span style={{
                            color: action.wouldExecute ? "#10b981" : "#6b7280"
                          }}>
                            {action.time} {action.wouldExecute ? "✓" : "(skipped)"}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            <button
              className="btn btn-secondary"
              style={{ marginTop: "16px" }}
              onClick={() => setTestResults(null)}
            >
              Clear Results
            </button>
          </div>
        )}
      </div>

      {showPrompt && (
        <TutorialPrompt
          module={shabbatTutorial.module}
          moduleName={shabbatTutorial.moduleName}
          steps={shabbatTutorial.steps}
          onClose={dismissTutorial}
        />
      )}
    </div>
  );
};

export default ShabbatSettings;
